import type { FormEvent } from 'react';
import { Button, CardSurface, Notice, TextField } from '@/components';
import { announce } from '@/lib/announce';
import { useInterfaceLanguage } from '@/lib/interface-language';

/**
 * One form, two modes (`FE-06.1`, `entry-surfaces.md` D7).
 * `docs/design/d1/D1-BRIEF.md` §6.3 and §7 fix every signature and every
 * accessible name below; this file does not invent either.
 *
 * ── Presentational, and "no hooks" but for the one context read ────────────
 * ⚠ Amended by `FE-80.1`. This file now holds exactly ONE hook —
 * `useInterfaceLanguage()`, a context READ that carries no state, fetches
 * nothing and needs no provider in a test (the context's own default
 * catalogue resolves every key). Every other claim in this paragraph still
 * holds verbatim; the sentence below about `useState` is about *component
 * state*, of which there is still none.
 * The route (agent G, wave 2) owns `GET /me`, the Better Auth calls and the
 * router — this file owns none of it, which is what keeps its own test free
 * of a `QueryClientProvider`. It also holds no component state: the email,
 * password and name fields are **uncontrolled** and read once, from
 * `FormData`, on submit. That is a deliberate reading of "no hooks" as
 * literal rather than "no data hooks" — a controlled form would need
 * `useState` for three fields and buys nothing a native `<form>` does not
 * already give for free (native `required` already blocks an empty
 * submission before this component's `onSubmit` handler ever runs).
 *
 * ── The name-length check has to be JS, not just `maxLength` ───────────────
 * `maxLength={128}` stops ordinary typing, but the HTML value-sanitisation
 * algorithm only clamps *user-typed* input — a value assigned
 * programmatically (a password manager's autofill, a paste path some
 * browsers do not clamp) can still exceed it, and Better Auth does not know
 * `users.display_name` is `varchar(128)`, so that member finds out with a
 * 422 after already submitting (`D1-BRIEF.md` §3.2). The check here uses the
 * platform's own Constraint Validation API
 * (`setCustomValidity` + `reportValidity`) rather than component state, for
 * the same "no hooks" reason above — it reports through the field's native
 * validation UI and is cleared immediately after, so it never becomes stale
 * state this component would otherwise have to track.
 *
 * ── The mode change is announced from here, not from the route ─────────────
 * `entry-surfaces.md` D11: one event, one channel. A sign-in **failure**
 * already renders `Notice tone="bad"`, which is `role="alert"` on its own —
 * so `announce()` must NOT also be called for that event (calling both is
 * the double-announcement D11 forbids, and per the brief's own accessibility
 * note it is the likeliest defect in this chunk). A **mode change** is the
 * opposite case: nothing else in this render marks it, so `announce()` is
 * the correct — and only — channel for it (D11's own table lists nothing
 * with a `role="alert"`/`role="status"` node of its own as *not* going
 * through `announce()`).
 *
 * The call is made *here*, in the toggle's own handler, rather than by the
 * route watching `mode` in a `useEffect`. A `useEffect` keyed on `mode` also
 * fires on the route's first mount — the route has to special-case "skip the
 * first run" to avoid announcing "Switched to sign in" the instant the page
 * loads, when nothing has actually changed yet. The toggle's click handler
 * only exists for an actual member-initiated change, so it needs no such
 * guard. This does not conflict with "no hooks": `announce()` is a plain
 * module function, not a hook, and calling it commits this component to
 * nothing a route composing it would need to unwind — see the return notes
 * for the seam this leaves for agent G to confirm.
 *
 * ── What this panel does not render, on purpose ─────────────────────────────
 * No "Forgot password?" link: there is no mail transport anywhere in the
 * tree and Better Auth's `/auth/forget-password` would accept the post and
 * then deliver nothing (`entry-surfaces.md` C5). No username field: a
 * username is derived from the email at sign-up and no route accepts one
 * (D7) — the account menu is where a member reads `@username` back.
 *
 * ── Surface composition ──────────────────────────────────────────────────
 * The panel itself sits on `CardSurface variant="sheet"`. `Notice` carries
 * its own opaque sheet and is rendered as this component's **sibling**,
 * never as a child wrapped by the `CardSurface` — nesting it would nest the
 * radius (`D1-BRIEF.md` §6.3, §8.1).
 */

export type SignInMode = 'sign-in' | 'create-account';

export interface SignInPanelProps {
  mode: SignInMode;
  onModeChange: (mode: SignInMode) => void;
  onSubmit: (credentials: { email: string; password: string; name: string }) => void;
  busy?: boolean;
  /** Already a member-facing sentence — this panel never maps a code. */
  errorMessage?: string;
  /** True when the member arrived here from a protected URL (`next` present). */
  redirected?: boolean;
  /**
   * Is self-registration open on this instance? Defaults to `true`, which is
   * every existing caller and every existing test.
   *
   * When `false` the create-account toggle is not rendered, and since that
   * button is the **only** thing that ever calls `onModeChange` — there is no
   * `?mode=` deep link, and the route's docstring records that one was
   * considered and rejected — the panel is then permanently in `sign-in` mode.
   * The route derives the value; this component states no policy of its own.
   *
   * ⚠ Cosmetic. `apps/server`'s `emailAndPassword.disableSignUp` is what makes
   * registration actually closed; hiding a button closes nothing. The two are
   * gated on different axes on purpose (`apps/server/src/config.ts`,
   * `signUpEnabled`).
   */
  signUpOpen?: boolean;
}

/** `users.display_name varchar(128)` — the bound Better Auth cannot see (§3.2). */
const MAX_NAME_LENGTH = 128;

/**
 * `FormData.get` returns `FormDataEntryValue | null` (`File | string | null`)
 * — every field here is a text input, so a `File` can never actually arrive,
 * but `String(file)` would silently stringify to `"[object File]"` rather
 * than error, which is the trap this guard exists for.
 */
function fieldValue(data: FormData, key: string): string {
  const value = data.get(key);
  return typeof value === 'string' ? value : '';
}

export function SignInPanel({
  mode,
  onModeChange,
  onSubmit,
  busy,
  errorMessage,
  redirected = false,
  signUpOpen = true,
}: SignInPanelProps) {
  const { t } = useInterfaceLanguage();
  const isCreateAccount = mode === 'create-account';

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    // `Button`'s own click guard already blocks a second click while busy
    // (it calls `preventDefault()` on the click, which cancels the implicit
    // form submission too), but that guarantee lives in a file this one does
    // not own. Enter-to-submit bypasses a button click entirely, so this
    // check is the one this component is actually responsible for.
    if (busy === true) return;
    const form = event.currentTarget;
    const data = new FormData(form);
    const email = fieldValue(data, 'email');
    const password = fieldValue(data, 'password');

    if (!isCreateAccount) {
      onSubmit({ email, password, name: '' });
      return;
    }

    const name = fieldValue(data, 'name');
    if (name.length > MAX_NAME_LENGTH) {
      const nameField = form.elements.namedItem('name');
      if (nameField instanceof HTMLInputElement) {
        nameField.setCustomValidity(
          t('signedOut.name.tooLong', { count: MAX_NAME_LENGTH }),
        );
        nameField.reportValidity();
        // Cleared immediately so a later, valid submission is not blocked by
        // a stale message — this field carries no state of its own to track
        // "has the member fixed it yet".
        nameField.setCustomValidity('');
      }
      return;
    }

    onSubmit({ email, password, name });
  }

  function handleModeToggle() {
    const nextMode: SignInMode = isCreateAccount ? 'sign-in' : 'create-account';
    announce(
      nextMode === 'create-account'
        ? t('signedOut.announce.createAccount')
        : t('signedOut.announce.signIn'),
    );
    onModeChange(nextMode);
  }

  return (
    <div className="mx-auto flex w-full max-w-sm flex-col gap-4">
      {redirected && <Notice tone="info" body={t('signedOut.redirectNotice')} />}
      {errorMessage ? <Notice tone="bad" body={errorMessage} /> : null}

      <CardSurface variant="sheet" className="flex flex-col gap-6 p-6">
        <h1 className="text-xl font-semibold text-ink">
          {isCreateAccount
            ? t('signedOut.createAccount.heading')
            : t('signedOut.signIn.heading')}
        </h1>

        <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
          {/*
            A plain layout `<fieldset>` (D7's "fieldset swap"), deliberately
            with no `<legend>`: the `h1` above already carries "Sign in" /
            "Create an account" once, and a legend would put a *third* copy
            of that string in the DOM (after the `h1` and the submit
            button's own label) for no reader benefit — this form is the
            page's only content, so the `h1` already answers "a group of
            what". A legend earns its place when a fieldset is one of
            several groups on a page; this one is not.
          */}
          <fieldset className="m-0 flex flex-col gap-4 border-0 p-0">
            <TextField
              label={t('signedOut.field.email')}
              name="email"
              type="email"
              autoComplete="email"
              required
            />

            <TextField
              label={t('signedOut.field.password')}
              name="password"
              type="password"
              autoComplete={isCreateAccount ? 'new-password' : 'current-password'}
              required
            />

            {isCreateAccount && (
              <TextField
                label={t('signedOut.field.name')}
                name="name"
                type="text"
                autoComplete="name"
                maxLength={MAX_NAME_LENGTH}
                required
              />
            )}
          </fieldset>

          <Button
            type="submit"
            variant="primary"
            busyLabel={
              isCreateAccount
                ? t('signedOut.createAccount.busy')
                : t('signedOut.signIn.busy')
            }
            {...(busy !== undefined ? { busy } : {})}
          >
            {isCreateAccount
              ? t('signedOut.createAccount.submit')
              : t('signedOut.signIn.submit')}
          </Button>
        </form>

        {/*
          Registration closed ⇒ no toggle at all, and deliberately nothing in
          its place. A "registration is closed" sentence would be a second
          static string on a surface whose whole job is one form, and it tells
          the only person who can read it — someone with no account — nothing
          they can act on. The heading above already says "Sign in", which is
          the honest description of what this page now offers.
        */}
        {signUpOpen && (
          <Button
            variant="quiet"
            size="sm"
            onClick={handleModeToggle}
            className="self-start"
          >
            {isCreateAccount
              ? t('signedOut.toggle.toSignIn')
              : t('signedOut.toggle.toCreateAccount')}
          </Button>
        )}
      </CardSurface>
    </div>
  );
}
